import type { AnyRouter } from '@tanstack/react-router'

// Hashed assets persist on the CDN, so a stale client never chunk-errors — it errors, if at all, on a PAYLOAD the
// new build reshaped (#88). Hence two triggers: the next navigation, and a crash while the drift is pending.

// Wire constant — kept in sync with apps/server/server.bun.ts (no shared package between the two).
export const APP_VERSION_HEADER = 'x-danchiano-app-version'

const RELOAD_GUARD_KEY = 'danchiano:version-reload-at'
const RELOAD_COOLDOWN_MS = 30_000

let latestSeenVersion = ''

function reloadedAtFor(key: string): number | null {
  try {
    const at = Number(window.sessionStorage.getItem(key))
    return Number.isFinite(at) && at > 0 ? at : null
  } catch {
    return null
  }
}

// A failed import here means a dead network, not a stale deploy, so pre-set TanStack's guard key and let the
// error boundary handle it — its own reload would replace the app with a blank chrome-error:// document.
export function suppressTanstackChunkReload(payload: unknown): void {
  const message = payload instanceof Error ? payload.message : typeof payload === 'string' ? payload : ''
  if (!message) return
  try {
    window.sessionStorage.setItem(`tanstack_router_reload:${message}`, '1')
  } catch {
    // TanStack's own guard read is wrapped in the same check, so it won't reload either.
  }
}

/** The deployed version this running document/bundle was served as (baked SSR-side; see __root). */
function loadedVersion(): string {
  return (typeof window !== 'undefined' && window.APP_VERSION) || ''
}

/** Wrap `window.fetch` once to record the version the server advertises on every response. */
function captureVersionFromFetch() {
  const original = window.fetch
  // Object.assign preserves the static members of `fetch` (e.g. `fetch.preconnect`) on the wrapper.
  window.fetch = Object.assign((...args: Parameters<typeof fetch>) => {
    return original(...args).then((response) => {
      const version = response.headers.get(APP_VERSION_HEADER)
      if (version) latestSeenVersion = version
      return response
    })
  }, original)
}

/**
 * Pure arm of {@link versionDriftPending}, so both of its answers are unit-testable: this document is running a
 * build the server has already replaced, and no reload has been spent on that drift yet. The cooldown is what
 * keeps a drift the reload does NOT resolve from looping — after one attempt the crash is today's build's.
 */
export function isVersionDriftPending(input: {
  loaded: string
  latestSeen: string
  reloadedAt: number | null
  now: number
}): boolean {
  if (input.loaded === '' || input.latestSeen === '' || input.latestSeen === input.loaded) return false
  return input.reloadedAt == null || input.now - input.reloadedAt >= RELOAD_COOLDOWN_MS
}

/** Always false where no version is stamped (dev / e2e build) — nothing advertises one to drift from. */
export function versionDriftPending(): boolean {
  return isVersionDriftPending({
    loaded: loadedVersion(),
    latestSeen: latestSeenVersion,
    reloadedAt: reloadedAtFor(RELOAD_GUARD_KEY),
    now: Date.now(),
  })
}

/** Guard first, then reload: a drift the new build does not resolve must cost exactly one reload. */
export function reloadForVersionDrift(): void {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    // Reload anyway: the worst case is one extra reload, which still lands the user on the new version.
  }
  window.location.reload()
}

// No-op when no version is stamped (dev / e2e build).
export function setupVersionDriftReload(router: AnyRouter) {
  if (loadedVersion() === '') return
  captureVersionFromFetch()
  router.subscribe('onResolved', () => {
    if (versionDriftPending()) reloadForVersionDrift()
  })
}
